// SentinelRouteTracking.jsx — Smooth real live tracking (walking + bike)
// Key fixes:
//   1. MAX_JUMP_METERS = 300 (was 60) — bike travel no longer breaks polyline
//   2. Removed dist-only segment break in drawLiveLayers — only time gap breaks segments
//   3. MAX_SPEED_MPS = 15 — allows bike up to ~54 km/h
//   4. Polyline redraws incrementally — new points animate in smoothly
//   5. Poll stays at 1s while guard is active — no aggressive backoff

import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import Layout from "../layouts/Layout";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const API_BASE_URL = "https://patrol-react.eblackdog.net";

const ICON = {
  pinSizeFrom: 44,
  pinSizeTo:   44,
  userSize:    48,
  cpRadius:    9,
};

// Poll settings — stays fast while guard is moving
const POLL_MIN_MS       = 2000;  // 2s minimum — fast enough for live feel
const POLL_MAX_MS       = 5000;  // 5s max when completed or no change
const POLL_BACKOFF_STEP = 500;

// Segment break — only split polyline on a TIME gap, not distance
// This fixes bike travel: guard on bike covers 100-200m per poll which
// was breaking the line into tiny invisible pieces with MAX_JUMP=60
const GAP_MINUTES = 5;           // break segment if no point for 5 min
const GAP_MS      = GAP_MINUTES * 60 * 1000;

// Filter thresholds — match the app's shouldAcceptGpsPoint()
const MAX_JUMP_METERS    = 300;  // rejects teleport (>300m instant jump)
const MAX_ACCURACY_METERS = 20;
const MAX_SPEED_MPS      = 15;   // ~54 km/h — covers bike comfortably

const MARKER_ANIMATION_MS = 800; // smooth marker slide

function pinSVG(fill = "#111827", stroke = "#111827", size = 40) {
  return `
  <svg viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="${stroke}" stroke-width="1.5" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 22s7-6.33 7-12a7 7 0 1 0-14 0c0 5.67 7 12 7 12z" fill="${fill}" opacity="0.14"/>
    <circle cx="12" cy="10" r="3.25" fill="${fill}" />
    <path d="M12 22s7-6.33 7-12a7 7 0 1 0-14 0c0 5.67 7 12 7 12z" />
  </svg>`;
}

function userSVG(color = "#2563EB", size = 44) {
  return `
  <svg viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="${color}" stroke-width="1.5" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="8.5" r="3.5" fill="${color}" opacity="0.12"/>
    <path d="M4 19a8 8 0 0 1 16 0" fill="${color}" opacity="0.08"/>
    <circle cx="12" cy="8.5" r="3.5"/>
    <path d="M4 19a8 8 0 0 1 16 0"/>
  </svg>`;
}

function pinIcon(fill, stroke, size, className = "pin") {
  return L.divIcon({
    className,
    html: pinSVG(fill, stroke, size),
    iconSize:   [size, size],
    iconAnchor: [size / 2, size],
  });
}

function userIcon(color, size, className = "userIcon") {
  return L.divIcon({
    className,
    html: `<div class="pulse"></div>${userSVG(color, size)}`,
    iconSize:   [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

function badgeIcon(text, bg) {
  return L.divIcon({
    className: "",
    html: `
      <div style="
        transform:translate(-50%,-50%);
        background:${bg};
        color:#fff;
        font-size:10px;
        font-weight:600;
        padding:2px 5px;
        border-radius:999px;
        border:1px solid rgba(0,0,0,.25);
        box-shadow:0 1px 3px rgba(0,0,0,.25);
        white-space:nowrap;
      ">${text}</div>`,
    iconSize:   [40, 18],
    iconAnchor: [20, 9],
  });
}

function loginIcon()  { return badgeIcon("LOGIN",  "#16a34a"); }
function logoutIcon() { return badgeIcon("LOGOUT", "#8b5cf6"); }

function parseDbIst(ts) {
  if (!ts) return null;
  if (ts instanceof Date) return ts;
  if (typeof ts !== "string") return null;
  const s = ts.trim();
  if (!s) return null;
  const clean = s.replace("T", " ").replace("Z", "").split(".")[0];
  const [datePart, timePart] = clean.split(" ");
  if (!datePart || !timePart) return null;
  const [year, month, day] = datePart.split("-").map(Number);
  const [hour, minute, second = 0] = timePart.split(":").map(Number);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day) ||
      !Number.isFinite(hour) || !Number.isFinite(minute)) return null;
  const d = new Date(year, month - 1, day, hour, minute, second);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDateObj(d) {
  if (!d) return "—";
  const day    = String(d.getDate()).padStart(2, "0");
  const month  = d.toLocaleString("en-IN", { month: "short" });
  const year   = d.getFullYear();
  let hour     = d.getHours();
  const minute = String(d.getMinutes()).padStart(2, "0");
  const second = String(d.getSeconds()).padStart(2, "0");
  const ampm   = hour >= 12 ? "pm" : "am";
  hour = hour % 12 || 12;
  return `${day} ${month} ${year}, ${String(hour).padStart(2, "0")}:${minute}:${second} ${ampm}`;
}

function formatISTDateTime(ts) {
  const d = parseDbIst(ts);
  if (!d) return ts || "—";
  return formatDateObj(d);
}

function formatISTFromUtc(ts) {
  if (!ts) return "—";
  if (ts instanceof Date) {
    if (Number.isNaN(ts.getTime())) return "—";
    return new Intl.DateTimeFormat("en-IN", {
      timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true,
    }).format(ts).replace(",", "");
  }
  if (typeof ts !== "string") return "—";
  const s = ts.trim();
  if (!s) return "—";
  const utcStr = /Z$|[+-]\d{2}:?\d{2}$/.test(s) ? s : `${s}Z`;
  const d = new Date(utcStr);
  if (Number.isNaN(d.getTime())) return ts || "—";
  return new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true,
  }).format(d).replace(",", "");
}

function isValidLatLng(point) {
  return point &&
    Number.isFinite(Number(point.lat)) &&
    Number.isFinite(Number(point.lng));
}

function haversineMeters(a, b) {
  const R = 6371000;
  const toRad = v => (v * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}

// ── cleanGpsTrack ─────────────────────────────────────────────────────────────
// Filters bad points. Uses dist+speed combined check — not dist alone.
// This means a bike covering 100m in 10s (10 m/s) passes fine.
// Only genuine GPS teleport jumps (>300m instantly) are rejected.
function cleanGpsTrack(rawTrack) {
  const ordered = rawTrack
    .filter(p => isValidLatLng(p))
    .map(p => ({
      ...p,
      lat:      Number(p.lat),
      lng:      Number(p.lng),
      accuracy: p.accuracy == null ? null : Number(p.accuracy),
    }))
    .filter(p => p.accuracy == null || p.accuracy <= MAX_ACCURACY_METERS)
    .sort((a, b) => {
      const da = parseDbIst(a.ts)?.getTime() ?? 0;
      const db = parseDbIst(b.ts)?.getTime() ?? 0;
      return da - db;
    });

  const cleaned = [];

  for (const p of ordered) {
    if (!cleaned.length) { cleaned.push(p); continue; }

    const prev = cleaned[cleaned.length - 1];

    // Skip exact duplicates
    if (prev.lat === p.lat && prev.lng === p.lng && prev.ts === p.ts) continue;

    const dist    = haversineMeters({ lat: prev.lat, lng: prev.lng }, { lat: p.lat, lng: p.lng });
    const prevMs  = parseDbIst(prev.ts)?.getTime() ?? null;
    const curMs   = parseDbIst(p.ts)?.getTime()  ?? null;
    const seconds = prevMs != null && curMs != null ? Math.max(1, (curMs - prevMs) / 1000) : 1;
    const speed   = dist / seconds;

    // Only reject if BOTH distance is huge AND speed is impossible
    // This keeps bike points (large dist, reasonable speed)
    // and rejects GPS teleport jumps (large dist, impossible speed)
    if (dist > MAX_JUMP_METERS && speed > MAX_SPEED_MPS) {
      console.log(`[MAP] Reject teleport id=${p.id} dist=${dist.toFixed(1)}m speed=${speed.toFixed(1)}m/s`);
      continue;
    }

    cleaned.push(p);
  }

  return cleaned;
}

function insideCircle(lat, lng, centerLat, centerLng, radiusM) {
  return haversineMeters({ lat, lng }, { lat: Number(centerLat), lng: Number(centerLng) }) <= Number(radiusM || 0);
}

function pointInPolygon(lat, lng, ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [latI, lngI] = ring[i];
    const [latJ, lngJ] = ring[j];
    const intersect = lngI > lng !== lngJ > lng &&
      lat < ((latJ - latI) * (lng - lngI)) / ((lngJ - lngI) || 1e-12) + latI;
    if (intersect) inside = !inside;
  }
  return inside;
}

function easeInOutQuad(t) {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

function animateMarker(marker, from, to, duration = MARKER_ANIMATION_MS) {
  if (!marker || !from || !to) return;
  const start = performance.now();
  function step(now) {
    const rawT = Math.min((now - start) / duration, 1);
    const t    = easeInOutQuad(rawT);
    marker.setLatLng([from[0] + (to[0] - from[0]) * t, from[1] + (to[1] - from[1]) * t]);
    if (rawT < 1) requestAnimationFrame(step);
  }
  requestAnimationFrame(step);
}

function getLocalDashboardData() {
  const userData = JSON.parse(localStorage.getItem("userData") || "{}");
  return {
    company_name:    localStorage.getItem("company_name")    || userData.company_name    || "",
    licensedFor:     localStorage.getItem("licensedFor")     || userData.licensedFor     || "",
    admin_active:    Number(localStorage.getItem("admin_active") ?? userData.admin_active ?? 0),
    company_address: localStorage.getItem("company_address") || userData.company_address || "",
    company_phone:   localStorage.getItem("company_phone")   || userData.company_phone   || "",
    admin_email:     localStorage.getItem("admin_email")     || userData.admin_email     || "",
    company_logo:    localStorage.getItem("company_logo")    || userData.company_logo    || "",
    user_map:        localStorage.getItem("user_map")        || userData.user_map        || "",
  };
}

export default function SentinelRouteTracking() {
  const location = useLocation();

  const mapRef               = useRef(null);
  const latestMarkerRef      = useRef(null);
  const userPathLayerRef     = useRef(null);
  const checkpointsLayerRef  = useRef(null);
  const geofenceLayerRef     = useRef(null);
  const routeLineLayerRef    = useRef(null);
  const routeEndPointsRef    = useRef(null);
  const loginLogoutLayerRef  = useRef(null);

  const pollTimeoutRef           = useRef(null);
  const currentPollIntervalRef   = useRef(POLL_MIN_MS);
  const lastPositionKeyRef       = useRef("");
  const isFetchingRef            = useRef(false);

  const didInitialFitRef         = useRef(false);
  const staticDrawnRef           = useRef(false);
  const lastTrackSignatureRef    = useRef("");
  const lastCompletionStatusRef  = useRef("");

  const dashboardData = useMemo(() => getLocalDashboardData(), []);
  const routePage     = useMemo(() => "/sentinel-route-master", []);

  const queryData = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      routeId: Number(params.get("route_id") || params.get("sentinel_route_id") || 0),
      userId:  params.get("user_id") || params.get("member_id") || "",
      allocId: Number(params.get("alloc_id") || params.get("allocation_id") || 0),
    };
  }, [location.search]);

  const apiUrl = useMemo(() => {
    const params = new URLSearchParams({
      route_id: String(queryData.routeId),
      limit:    "5000",
    });
    if (queryData.userId)  params.set("user_id",   queryData.userId);
    if (queryData.allocId) params.set("alloc_id",  String(queryData.allocId));
    return `${API_BASE_URL}/api/sentinel_route_tracking?${params.toString()}`;
  }, [queryData]);

  const setStatus = useCallback((id, text, type = "neutral") => {
    const el = document.getElementById(id);
    if (!el) return;
    el.textContent = text;
    const base    = "px-3 py-1 text-xs font-semibold rounded-full";
    const classes = { ok: "bg-emerald-100 text-emerald-700", warn: "bg-red-100 text-red-700", neutral: "bg-slate-200 text-slate-800" };
    el.className  = `${base} ${classes[type] || classes.neutral}`;
  }, []);

  const fenceStatusSet = useCallback((text, cls = "neutral") => setStatus("fenceStatus", text, cls), [setStatus]);
  const routeStatusSet = useCallback((text, cls = "neutral") => setStatus("routeStatus", text, cls), [setStatus]);

  const applyCompletionStatus = useCallback(completion => {
    if (!completion?.status) { routeStatusSet("Route status: —", "neutral"); return; }
    const status = String(completion.status || "").toUpperCase();
    const label  = completion.label || "";
    let cls = "neutral";
    if (["EARLY", "ONTIME", "COMPLETED"].includes(status)) cls = "ok";
    if (["DELAY", "PENDING_DELAY"].includes(status))       cls = "warn";
    routeStatusSet(label || `Route status: ${status}`, cls);
  }, [routeStatusSet]);

  const clearLayers = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    [userPathLayerRef, checkpointsLayerRef, geofenceLayerRef, routeLineLayerRef,
     routeEndPointsRef, loginLogoutLayerRef, latestMarkerRef].forEach(ref => {
      if (ref.current) { map.removeLayer(ref.current); ref.current = null; }
    });
    lastTrackSignatureRef.current   = "";
    lastCompletionStatusRef.current = "";
  }, []);

  const drawStaticLayers = useCallback(data => {
    const map = mapRef.current;
    if (!map) return;

    const bounds  = L.latLngBounds();
    const from    = data.route?.meta?.from || null;
    const to      = data.route?.meta?.to   || null;
    const endMarkers = [];

    if (isValidLatLng(from)) {
      const lat = Number(from.lat), lng = Number(from.lng);
      endMarkers.push(L.marker([lat, lng], { icon: pinIcon("#16a34a", "#166534", ICON.pinSizeFrom) }).bindTooltip("From", { direction: "top" }));
      bounds.extend([lat, lng]);
    }
    if (isValidLatLng(to)) {
      const lat = Number(to.lat), lng = Number(to.lng);
      endMarkers.push(L.marker([lat, lng], { icon: pinIcon("#dc2626", "#991b1b", ICON.pinSizeTo) }).bindTooltip("To", { direction: "top" }));
      bounds.extend([lat, lng]);
    }
    if (endMarkers.length) routeEndPointsRef.current = L.layerGroup(endMarkers).addTo(map);

    const routeLine = data.route?.line?.geometry?.coordinates || [];
    if (Array.isArray(routeLine) && routeLine.length >= 2) {
      const coordsLL = routeLine
        .map(([lng, lat]) => [Number(lat), Number(lng)])
        .filter(([lat, lng]) => Number.isFinite(lat) && Number.isFinite(lng));
      if (coordsLL.length >= 2) {
        routeLineLayerRef.current = L.polyline(coordsLL, {
          color: "#0ea5e9", weight: 4, opacity: 0.7, dashArray: "6,8", smoothFactor: 2,
        }).addTo(map);
        coordsLL.forEach(c => bounds.extend(c));
      }
    }

    const cps = Array.isArray(data.checkpoints) ? data.checkpoints : [];
    const cpMarkers = cps.filter(cp => isValidLatLng(cp)).map(cp => {
      const lat = Number(cp.lat), lng = Number(cp.lng);
      bounds.extend([lat, lng]);
      return L.circleMarker([lat, lng], {
        radius: ICON.cpRadius, color: "#16a34a", weight: 2,
        opacity: 0.95, fillColor: "#22c55e", fillOpacity: 0.35,
      }).bindTooltip(cp.checkpoint_name || "Checkpoint", { direction: "top" });
    });
    if (cpMarkers.length) checkpointsLayerRef.current = L.layerGroup(cpMarkers).addTo(map);

    const gf = data.geofences?.features || [];
    const gfShapes = [];
    if (Array.isArray(gf) && gf.length) {
      for (const f of gf) {
        const p = f.properties || {};
        if (p.type === "CIRCLE" && f.geometry?.type === "Point") {
          const [lngRaw, latRaw] = f.geometry.coordinates || [];
          const lat = Number(latRaw), lng = Number(lngRaw);
          if (Number.isFinite(lat) && Number.isFinite(lng)) {
            gfShapes.push(L.circle([lat, lng], { radius: Number(p.radius_m || 0), color: "#f59e0b", weight: 2, fillOpacity: 0.08 })
              .bindPopup(`<b>${p.name || "Fence"}</b><br>Circle ${p.radius_m || 0}m`));
            bounds.extend([lat, lng]);
          }
        }
        if (p.type === "POLYGON" && f.geometry?.type === "Polygon") {
          const ring = (f.geometry.coordinates?.[0] || [])
            .map(([lng1, lat1]) => [Number(lat1), Number(lng1)])
            .filter(([la, ln]) => Number.isFinite(la) && Number.isFinite(ln));
          if (ring.length) {
            gfShapes.push(L.polygon(ring, { color: "#ef4444", weight: 2, fillOpacity: 0.06 })
              .bindPopup(`<b>${p.name || "Fence"}</b><br>Polygon`));
            ring.forEach(c => bounds.extend(c));
          }
        }
      }
      if (gfShapes.length) geofenceLayerRef.current = L.layerGroup(gfShapes).addTo(map);
    }

    staticDrawnRef.current = true;
    if (bounds.isValid() && !didInitialFitRef.current) {
      map.fitBounds(bounds.pad(0.15));
      didInitialFitRef.current = true;
    }
  }, []);

  const drawLiveLayers = useCallback(data => {
    const map    = mapRef.current;
    if (!map) return false;

    const bounds  = L.latLngBounds();
    const guards  = data.gps?.guards || [];
    const completion      = data.completion || null;
    const completionStatus = completion ? String(completion.status || "").toUpperCase() : null;
    const isCompleted = completionStatus !== null && completionStatus !== "IN_PROGRESS" &&
      ["EARLY", "ONTIME", "DELAY", "COMPLETED"].includes(completionStatus);
    const completionChanged = completionStatus !== lastCompletionStatusRef.current;
    if (completionChanged) lastCompletionStatusRef.current = completionStatus || "";

    let hasTrack               = false;
    let latestLLForSession     = null;
    let latestTsRawForSession  = null;
    let positionChanged        = false;

    for (const g of guards) {
      const rawTrack     = Array.isArray(g.track) ? g.track : [];
      const orderedTrack = cleanGpsTrack(rawTrack);
      if (!orderedTrack.length) continue;

      hasTrack = true;

      // ── Build segments ────────────────────────────────────────────────────
      // Only break on TIME GAP — not distance.
      // This is the main fix: bike travel covers large distances between points
      // but those should be ONE continuous segment, not broken pieces.
      const segments = [];
      let current    = null;

      orderedTrack.forEach(p => {
        const t   = parseDbIst(p.ts);
        const ms  = t ? t.getTime() : null;
        const pt  = [p.lat, p.lng];

        if (!current) {
          current = { points: [pt], firstTs: p.ts, lastTs: p.ts, lastMs: ms, lastPt: pt };
          return;
        }

        // Only break segment on TIME gap — not distance
        // A bike rider moving fast still belongs in the same segment
        const timeGap = ms !== null && current.lastMs !== null && ms - current.lastMs > GAP_MS;

        if (timeGap) {
          segments.push(current);
          current = { points: [pt], firstTs: p.ts, lastTs: p.ts, lastMs: ms, lastPt: pt };
        } else {
          current.points.push(pt);
          current.lastTs = p.ts;
          current.lastMs = ms !== null ? ms : current.lastMs;
          current.lastPt = pt;
        }
      });

      if (current?.points.length) segments.push(current);
      if (!segments.length) continue;

      const allPoints  = segments.flatMap(s => s.points);
      const firstTs    = segments[0].firstTs;
      const lastSeg    = segments[segments.length - 1];
      const lastTs     = lastSeg.lastTs;
      if (!allPoints.length) continue;

      const firstPt        = allPoints[0];
      const lastPt         = allPoints[allPoints.length - 1];
      const trackSignature = `${allPoints.length}-${lastTs}`;
      const posKey         = `${lastPt[0].toFixed(6)},${lastPt[1].toFixed(6)},${lastTs}`;

      if (posKey !== lastPositionKeyRef.current) {
        positionChanged          = true;
        lastPositionKeyRef.current = posKey;
      }

      const needsTrackRedraw = trackSignature !== lastTrackSignatureRef.current ||
        !userPathLayerRef.current ||
        (userPathLayerRef.current && !map.hasLayer(userPathLayerRef.current)) ||
        completionChanged;

      if (needsTrackRedraw) {
        if (userPathLayerRef.current) { map.removeLayer(userPathLayerRef.current); userPathLayerRef.current = null; }

        if (allPoints.length >= 2) {
          // Draw each segment as its own polyline so time-gap breaks are clear
          const polylines = segments
            .filter(s => s.points.length >= 2)
            .map(s => L.polyline(s.points, { weight: 5, opacity: 0.9, color: "#0ea5e9", smoothFactor: 1 }));

          // Single-point segments get a dot
          const dots = segments
            .filter(s => s.points.length === 1)
            .map(s => L.circleMarker(s.points[0], { radius: 4, color: "#0ea5e9", fillColor: "#0ea5e9", fillOpacity: 0.8 }));

          userPathLayerRef.current = L.layerGroup([...polylines, ...dots]).addTo(map);
          allPoints.forEach(c => bounds.extend(c));
        } else if (allPoints.length === 1) {
          userPathLayerRef.current = L.layerGroup([
            L.circleMarker(allPoints[0], { radius: 5, color: "#0ea5e9", fillColor: "#0ea5e9", fillOpacity: 0.9 }),
          ]).addTo(map);
          bounds.extend(allPoints[0]);
        }

        lastTrackSignatureRef.current = trackSignature;
      } else {
        allPoints.forEach(c => bounds.extend(c));
      }

      // ── Login / Logout badges ─────────────────────────────────────────────
      if (completionChanged || !loginLogoutLayerRef.current) {
        const llMarkers = [];
        llMarkers.push(L.marker(firstPt, { icon: loginIcon() })
          .bindTooltip(`Login<br>${formatISTFromUtc(firstTs)}`, { direction: "top" }));
        bounds.extend(firstPt);

        if (isCompleted) {
          llMarkers.push(L.marker(lastPt, { icon: logoutIcon() })
            .bindTooltip(`Logout<br>${formatISTFromUtc(completion?.completed_at || lastTs)}`, { direction: "top" }));
          bounds.extend(lastPt);
        }

        if (loginLogoutLayerRef.current) { map.removeLayer(loginLogoutLayerRef.current); loginLogoutLayerRef.current = null; }
        loginLogoutLayerRef.current = L.layerGroup(llMarkers).addTo(map);
      }

      latestLLForSession    = [lastPt[0], lastPt[1]];
      latestTsRawForSession = lastTs;

      const idText   = g.guard_id   || g.member_id || g.user_id || "";
      const nameText = g.guard_name || g.guard_label || g.username || idText || "User";
      const timeIST  = formatISTFromUtc(lastTs);
      const lines    = [];
      if (nameText) lines.push(`Name: ${nameText}`);
      if (idText && idText !== nameText) lines.push(`ID: ${idText}`);
      if (timeIST) lines.push(`Time (IST): ${timeIST}`);

      // ── Live position marker ──────────────────────────────────────────────
      if (isCompleted) {
        if (latestMarkerRef.current) { map.removeLayer(latestMarkerRef.current); latestMarkerRef.current = null; }
      } else {
        if (!latestMarkerRef.current) {
          latestMarkerRef.current = L.marker(lastPt, {
            icon: userIcon("#2563EB", ICON.userSize), zIndexOffset: 1200,
          }).addTo(map);
        } else {
          const old   = latestMarkerRef.current.getLatLng();
          const oldPt = [old.lat, old.lng];
          animateMarker(latestMarkerRef.current, oldPt, lastPt, MARKER_ANIMATION_MS);
        }
        latestMarkerRef.current.bindTooltip(lines.join("<br>"), { direction: "top" });
        bounds.extend(lastPt);
      }
    }

    // ── Guard info panel ──────────────────────────────────────────────────
    const guardInfoEl = document.getElementById("guardInfo");
    if (guardInfoEl) {
      const g = guards[0];
      if (g) {
        const id   = g.guard_id || g.member_id || g.user_id || "";
        const name = g.guard_name || g.guard_label || g.username || id || "";
        guardInfoEl.textContent = `Guard: ${name && id && name !== id ? `${name} (${id})` : name || id || "—"}`;
      } else {
        guardInfoEl.textContent = "Guard: —";
      }
    }

    const lastTsEl = document.getElementById("lastTs");
    if (lastTsEl) lastTsEl.textContent = `Last update (IST): ${formatISTFromUtc(latestTsRawForSession)}`;

    if (completionStatus)    applyCompletionStatus(completion);
    else if (hasTrack)       routeStatusSet("Route status: In progress", "neutral");
    else                     routeStatusSet("Route status: —", "neutral");

    // ── Geofence status ───────────────────────────────────────────────────
    const gf     = data.geofences?.features || [];
    let inside   = false;
    if (latestLLForSession && Array.isArray(gf) && gf.length) {
      const [lat, lng] = latestLLForSession;
      for (const f of gf) {
        const p = f.properties || {};
        if (p.type === "CIRCLE" && f.geometry?.type === "Point") {
          const [clng, clat] = f.geometry.coordinates || [];
          if (Number.isFinite(Number(clat)) && Number.isFinite(Number(clng)) &&
              insideCircle(lat, lng, clat, clng, p.radius_m || 0)) { inside = true; break; }
        }
        if (p.type === "POLYGON" && f.geometry?.type === "Polygon") {
          const ring = (f.geometry.coordinates?.[0] || [])
            .map(([ln, la]) => [Number(la), Number(ln)])
            .filter(([la, ln]) => Number.isFinite(la) && Number.isFinite(ln));
          if (ring.length && pointInPolygon(lat, lng, ring)) { inside = true; break; }
        }
      }
    }

    if (!Array.isArray(gf) || !gf.length) fenceStatusSet("No fence", "neutral");
    else if (inside)                       fenceStatusSet("Inside geofence", "ok");
    else                                   fenceStatusSet("Outside geofence", "warn");

    if (bounds.isValid() && !didInitialFitRef.current) {
      map.fitBounds(bounds.pad(0.15));
      didInitialFitRef.current = true;
    }

    return positionChanged;
  }, [applyCompletionStatus, fenceStatusSet, routeStatusSet]);

  const updatePollInterval = useCallback((positionChanged, isCompleted) => {
    if (isCompleted) {
      currentPollIntervalRef.current = POLL_MAX_MS;
    } else if (positionChanged) {
      // Guard moved — stay fast
      currentPollIntervalRef.current = POLL_MIN_MS;
    } else {
      // No movement — slowly back off but not too much
      currentPollIntervalRef.current = Math.min(
        currentPollIntervalRef.current + POLL_BACKOFF_STEP,
        POLL_MAX_MS
      );
    }
    const el = document.getElementById("pollIndicator");
    if (el) el.textContent = `↻ ${(currentPollIntervalRef.current / 1000).toFixed(1)}s`;
  }, []);

  const drawAll = useCallback(data => {
    const map = mapRef.current;
    if (!map) return { positionChanged: false, isCompleted: false };

    if (!staticDrawnRef.current) drawStaticLayers(data);

    const positionChanged    = drawLiveLayers(data);
    const completionStatus   = data.completion ? String(data.completion.status || "").toUpperCase() : null;
    const isCompleted        = ["EARLY", "ONTIME", "DELAY", "COMPLETED"].includes(completionStatus);

    const sessionRangeEl = document.getElementById("sessionRange");
    if (sessionRangeEl) {
      const alloc    = data.allocation || null;
      const range    = data.range      || null;
      const startRaw = alloc?.start_time || range?.start || null;
      const endRaw   = alloc?.end_time   || range?.end   || null;
      sessionRangeEl.textContent = `Session: ${startRaw ? formatISTDateTime(startRaw) : "—"} → ${endRaw ? formatISTDateTime(endRaw) : "—"}`;
    }

    return { positionChanged: positionChanged ?? false, isCompleted };
  }, [drawStaticLayers, drawLiveLayers]);

  const refresh = useCallback(async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    let positionChanged = false;
    let isCompleted     = false;

    try {
      const response    = await fetch(apiUrl, { cache: "no-store" });
      const contentType = response.headers.get("content-type") || "";
      if (!contentType.includes("application/json")) {
        const raw = await response.text();
        console.error("Non-JSON response:", raw.slice(0, 500));
        throw new Error(`API returned non-JSON (${response.status})`);
      }
      const data = await response.json();
      if (!response.ok || !data.ok) throw new Error(data?.error || data?.message || `HTTP ${response.status}`);

      const result  = drawAll(data);
      positionChanged = result.positionChanged;
      isCompleted     = result.isCompleted;
    } catch (error) {
      fenceStatusSet(error.message || "API error", "warn");
      console.error("[sentinel_route_tracking] fetch failed:", error);
    } finally {
      isFetchingRef.current = false;
      updatePollInterval(positionChanged, isCompleted);
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = setTimeout(() => refresh(), currentPollIntervalRef.current);
    }
  }, [apiUrl, drawAll, fenceStatusSet, updatePollInterval]);

  useEffect(() => {
    if (!queryData.routeId) return;

    if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }

    didInitialFitRef.current        = false;
    staticDrawnRef.current          = false;
    lastTrackSignatureRef.current   = "";
    lastCompletionStatusRef.current = "";
    lastPositionKeyRef.current      = "";
    currentPollIntervalRef.current  = POLL_MIN_MS;

    const map = L.map("map", { zoomControl: true }).setView([20.5937, 78.9629], 5);
    mapRef.current = map;

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 20, attribution: "&copy; OpenStreetMap",
    }).addTo(map);

    L.control.scale({ imperial: false }).addTo(map);

    const legend = L.control({ position: "bottomright" });
    legend.onAdd = () => {
      const d = L.DomUtil.create("div", "legend");
      d.innerHTML = `
        <div><span class="chip" style="background:#0ea5e9"></span>Track</div>
        <div><span class="chip" style="background:#ef4444"></span>Fence Polygon</div>
        <div><span class="chip" style="background:#f59e0b"></span>Fence Circle</div>
        <div><span class="chip" style="background:#22c55e"></span>Checkpoints</div>
        <div><span style="display:inline-block;width:14px;height:14px;background:#16a34a;border-radius:3px;"></span> From</div>
        <div><span style="display:inline-block;width:14px;height:14px;background:#dc2626;border-radius:3px;"></span> To</div>
        <div><span style="display:inline-block;width:14px;height:14px;background:#2563EB;border-radius:50%;"></span> Live user</div>
        <div><span style="display:inline-block;width:14px;height:14px;background:#16a34a;border-radius:999px;"></span> Login</div>
        <div><span style="display:inline-block;width:14px;height:14px;background:#8b5cf6;border-radius:999px;"></span> Logout</div>
      `;
      return d;
    };
    legend.addTo(map);

    refresh();

    return () => {
      if (pollTimeoutRef.current) { clearTimeout(pollTimeoutRef.current); pollTimeoutRef.current = null; }
      clearLayers();
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    };
  }, [queryData.routeId, refresh, clearLayers]);

  if (!queryData.routeId) {
    return (
      <Layout dashboardData={dashboardData} routePage={routePage} activePage="sentinel-tracking" activeMenu="sentinel-tracking">
        <div className="min-h-[calc(100vh-90px)] bg-slate-100 p-6">
          <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-sm font-medium text-red-700 shadow-sm">
            route_id is required. Example: <span className="font-bold text-red-900">?route_id=123</span>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout dashboardData={dashboardData} routePage={routePage} activePage="sentinel-tracking" activeMenu="sentinel-tracking">
      <style>{`
        #map { height: 100%; width: 100%; min-height: 520px; }

        .legend {
          background: #fff;
          padding: 10px 12px;
          border-radius: 10px;
          box-shadow: 0 2px 12px rgba(0,0,0,.12);
          font: 12px/1.25 system-ui;
        }
        .legend div { margin: 6px 0; display: flex; align-items: center; gap: 8px; }
        .chip { display: inline-block; width: 14px; height: 14px; border-radius: 3px; }
        .pin      { transform: translate(-50%, -100%); }
        .userIcon { transform: translate(-50%, -50%); }

        .pulse {
          position: absolute;
          top: 50%; left: 50%;
          width: 18px; height: 18px;
          border-radius: 999px;
          transform: translate(-50%, -50%);
          animation: pulse 1.6s infinite;
        }
        @keyframes pulse {
          0%   { box-shadow: 0 0 0 0px rgba(37,99,235,.55); }
          70%  { box-shadow: 0 0 0 18px rgba(37,99,235,0); }
          100% { box-shadow: 0 0 0 0px rgba(37,99,235,0); }
        }
      `}</style>

      <div className="min-h-[calc(100vh-90px)] bg-gradient-to-br from-slate-100 via-slate-50 to-slate-100 p-4 md:p-6">
        <div className="flex h-[calc(100vh-120px)] min-h-[650px] flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl">

          <header className="border-b border-slate-200 bg-white px-4 py-4 md:px-6">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-400">Live Tracking</p>
                <h1 className="mt-1 text-xl font-bold text-slate-900 md:text-2xl">
                  Sentinel Route #{queryData.routeId}
                  {queryData.allocId ? <span className="text-slate-500"> — Allocation #{queryData.allocId}</span> : null}
                </h1>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <div id="guardInfo"    className="rounded-full bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-700">Guard: —</div>
                <div id="sessionRange" className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-600">Session: —</div>
                <div id="routeStatus"  className="rounded-full bg-slate-200 px-3 py-1 text-xs font-semibold text-slate-800">Route status: —</div>
                <div id="fenceStatus"  className="rounded-full bg-slate-200 px-3 py-1 text-xs font-semibold text-slate-800">No fence</div>
                <div id="lastTs"       className="rounded-full bg-slate-900 px-3 py-1.5 text-xs font-medium text-white">—</div>
                <div id="pollIndicator" title="Live refresh interval" className="rounded-full bg-emerald-600 px-3 py-1 text-xs font-semibold text-white">↻ 2.0s</div>
              </div>
            </div>
          </header>

          <div className="relative flex-1 bg-slate-200">
            <div id="map" />
          </div>

        </div>
      </div>
    </Layout>
  );
}